// src/pages/Dashboard.jsx
import React, { useState, useEffect } from "react";
import {
  BarChart3,
  Cpu,
  CreditCard,
  Settings,
  LogOut,
  Gauge,
  Bell,
  ChevronDown,
  Menu,
  X, // Close icon
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext"; // 按你的实际路径
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { User } from "lucide-react";
const LIST_API_URL =
  "https://tsp.turing-agi.com:442/prod-api/servicer/ticket/pageList?pageNum=1&pageSize=10";

const ADD_API_URL =
  "https://tsp.turing-agi.com:442/prod-api/servicer/ticket/add";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const displayName = user?.displayName || "";
  const email = user?.email || "Unknown user";
  const userLabel = displayName || email;

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [ticketTotal, setTicketTotal] = useState(0);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  // 新建 Ticket 相关
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createSuccess, setCreateSuccess] = useState("");
  const [form, setForm] = useState({
    customId: "",
    customName: "",
    contactPersonFirstName: "",
    contactPersonLastName: "",
    contactPersonEmail: "",
    deviceSn: "",
    problemDescription: "",
  });

  const [showCreateModal, setShowCreateModal] = useState(false);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const res = await fetch(LIST_API_URL);
      const data = await res.json();
      if (data && data.code === 200) {
        setTicketTotal(data.total || 0);
        setTickets(data.rows || []);
        setError(null);
      } else {
        setError((data && data.msg) || "API returned an error");
      }
    } catch (err) {
      setError(err.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const fakeCompute = [
    { m: "May-25", od: 20, sp: 10, spot: 5 },
    { m: "Jun-25", od: 32, sp: 13, spot: 6 },
    { m: "Jul-25", od: 38, sp: 15, spot: 7 },
    { m: "Aug-25", od: 42, sp: 14, spot: 8 },
    { m: "Sep-25", od: 45, sp: 17, spot: 10 },
    { m: "Oct-25", od: 48, sp: 18, spot: 12 },
    { m: "Nov-25", od: 52, sp: 16, spot: 11 },
  ];

  useEffect(() => {
    loadTickets();
  }, []);

  const handleLogout = async () => {
    try {
      localStorage.removeItem("token");
      await signOut(auth);
    } catch (err) {
      console.error("Error during logout:", err);
    } finally {
      navigate("/");
    }
  };

  const handleDesktopMenuClick = () => {
    setSidebarCollapsed((prev) => !prev);
  };

  const handleMobileMenuClick = () => {
    setMobileNavOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    setCreateSuccess("");

    try {
      const token = localStorage.getItem("token") || "";

      const res = await fetch(ADD_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: token } : {}),
        },
        body: JSON.stringify({
          custom_id: form.customId,
          custom_name: form.customName,
          contact_person_first_name: form.contactPersonFirstName,
          contact_person_last_name: form.contactPersonLastName,
          contact_person_email: form.contactPersonEmail,
          device_SN: form.deviceSn,
          problem_desc: form.problemDescription,
        }),
      });

      const data = await res.json();

      if (data.code === 200) {
        setCreateSuccess("Ticket created successfully!");
        setForm({
          customId: "",
          customName: "",
          contactPersonFirstName: "",
          contactPersonLastName: "",
          contactPersonEmail: "",
          deviceSn: "",
          problemDescription: "",
        });
        loadTickets();
        setShowCreateModal(false);
      } else {
        setCreateError(data.msg || "Failed to create ticket");
      }
    } catch (err) {
      setCreateError(err.message || "Request failed");
    } finally {
      setCreating(false);
      setTimeout(() => setCreateSuccess(""), 2000);
    }
  };

  const openTicketCount = tickets.filter(
    (t) => t.ticketStatus === "Open"
  ).length;
  const closedTicketCount = tickets.filter(
    (t) => t.ticketStatus === "Closed"
  ).length;

  return (
    <div className="min-h-screen flex bg-slate-950 text-slate-50">
      {/* 桌面端左侧导航栏（可折叠） */}
      <aside
        className={`hidden md:flex bg-gradient-to-b from-purple-800 to-slate-950 border-r border-purple-700/40 flex-col overflow-hidden transition-all duration-300 ease-in-out ${
          sidebarCollapsed ? "w-0 opacity-0" : "w-64 opacity-100"
        }`}
      >
        {!sidebarCollapsed && (
          <SidebarContent
            onClose={() => setMobileNavOpen(false)}
            onLogout={handleLogout}
            userLabel={userLabel}
          />
        )}
      </aside>

      {/* 移动端侧边抽屉导航 */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          <div
            className="flex-1 bg-black/50"
            onClick={() => setMobileNavOpen(false)}
          />
          <div className="w-64 bg-gradient-to-b from-purple-800 to-slate-950 border-r border-purple-700/40 flex flex-col animate-[slideIn_0.2s_ease-out]">
            <SidebarContent
              onClose={() => setMobileNavOpen(false)}
              onLogout={handleLogout}
              userLabel={userLabel}
            />
          </div>
        </div>
      )}

      {/* 右侧主内容 */}
      <main className="flex-1 flex flex-col">
        {/* 顶部栏 */}
        <header className="h-16 border-b border-slate-800/80 flex items-center px-4 md:px-6 bg-slate-950/80 backdrop-blur">
          {/* 左边：菜单 + Title */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              className="w-9 h-9 rounded-full border border-slate-700 flex items-center justify-center hover:border-purple-500 transition md:hidden"
              onClick={handleMobileMenuClick}
            >
              <Menu size={18} className="text-white" />
            </button>

            <button
              className="hidden md:flex w-9 h-9 rounded-full border border-slate-700 items-center justify-center hover:border-purple-500 transition"
              onClick={handleDesktopMenuClick}
            >
              <Menu size={18} className="text-white" />
            </button>

            {/* 移动端 Logo */}
            <div className="flex items-center gap-2 md:hidden">
              <div className="w-8 h-8 rounded-2xl bg-purple-500 flex items-center justify-center text-sm font-bold">
                AI
              </div>
              <div>
                <p className="text-xs font-semibold tracking-wide">AI NOVA</p>
                <p className="text-[10px] text-purple-200/80">
                  Compute Dashboard
                </p>
              </div>
            </div>

            <div>
              <h1 className="text-base md:text-lg font-semibold tracking-tight">
                Compute Summary - Previous Month
              </h1>
              <p className="text-[10px] md:text-xs text-slate-400">
                Ticket Dashboard · AWS EC2 · Multi-account
              </p>
            </div>
          </div>

          {/* 右边：让整个右侧按钮组靠右 */}
          <div className="ml-auto flex items-center gap-2">
            {/* Export Button */}
            <button className="hidden sm:inline-flex px-3 py-1.5 rounded-lg border border-slate-700 hover:border-purple-500 hover:text-purple-200 transition">
              Export Report
            </button>

            {/* Profile Button */}
            <div className="relative">
              <button
                className="w-9 h-9 rounded-full border border-slate-700 flex items-center justify-center hover:border-purple-500 transition"
                onClick={() => setProfileOpen((prev) => !prev)}
              >
                <User size={16} />
              </button>

              {profileOpen && (
                <div className="absolute right-0 mt-2 w-44 bg-slate-900 border border-slate-700 rounded-xl shadow-lg p-2 z-50">
                  <p className="px-3 py-2 text-[12px] text-slate-300 border-b border-slate-700">
                    {userLabel}
                  </p>

                  <button className="w-full text-left px-3 py-2 text-sm text-slate-200 rounded-lg hover:bg-slate-800">
                    Edit Profile
                  </button>

                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-3 py-2 text-sm text-red-400 rounded-lg hover:bg-red-500/20"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* 内容区域 */}
        <div className="flex-1 p-4 md:p-6 space-y-6 overflow-auto bg-gradient-to-br from-slate-950 via-slate-900 to-purple-950">
          {/* 顶部 KPI 卡片 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              title="Tickets (Total)"
              primary={ticketTotal}
              secondary="Total Tickets from API"
              badge={ticketTotal ? "Live" : ""}
            />
            <KpiCard
              title="Open Tickets"
              primary={openTicketCount}
              secondary="Ticket Status: Open"
            />
            <KpiCard
              title="Closed Tickets"
              primary={closedTicketCount}
              secondary="Ticket Status: Closed"
            />
            <KpiCard
              title="API Status"
              primary={loading ? "Loading..." : error ? "Error" : "OK"}
              secondary={error || "Status"}
              variant={error ? "danger" : "default"}
            />
          </div>

          {/* 中间：图表区域（保留原 design） */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            {/* 左边：假图表 */}
            <div className="xl:col-span-2 bg-slate-900/70 border border-slate-800 rounded-2xl p-4">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div>
                  <h2 className="text-sm font-semibold">EC2 Compute Cost</h2>
                  <p className="text-[11px] text-slate-400">
                    Grouped by billing period (month)
                  </p>
                </div>
                <div className="flex gap-2 text-[11px]">
                  <LegendDot label="OnDemand" />
                  <LegendDot label="SavingsPlan" />
                  <LegendDot label="Spot" />
                </div>
              </div>

              <div className="h-56 mt-2 rounded-xl bg-[radial-gradient(circle_at_0_0,_rgba(168,85,247,0.25),_transparent_55%),radial-gradient(circle_at_60%_0,_rgba(59,130,246,0.25),_transparent_55%)] border border-slate-800/80 px-4 pb-4 flex items-end gap-3">
                {fakeCompute.map((item) => (
                  <div
                    key={item.m}
                    className="flex-1 flex flex-col items-center"
                  >
                    {/* 故意用 px → 绝对保证显示 */}
                    <div className="flex flex-col justify-end gap-1 w-full">
                      <div
                        className="bg-purple-500/70 rounded-t-md"
                        style={{ height: item.od * 2 }}
                      />
                      <div
                        className="bg-emerald-400/70 rounded-t-md"
                        style={{ height: item.sp * 2 }}
                      />
                      <div
                        className="bg-sky-400/70 rounded-t-md"
                        style={{ height: item.spot * 2 }}
                      />
                    </div>

                    <span className="text-[10px] text-slate-400 mt-2">
                      {item.m}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* 右边：环形统计块 */}
            <div className="space-y-4">
              <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-4 flex flex-col items-center">
                <p className="text-xs text-slate-400 mb-2">
                  EC2 Coverage (Hours)
                </p>
                <div className="relative w-28 h-28 md:w-32 md:h-32">
                  <div className="absolute inset-0 rounded-full border-[9px] md:border-[10px] border-purple-500/70" />
                  <div className="absolute inset-3 rounded-full border-[7px] md:border-[8px] border-emerald-400/70" />
                  <div className="absolute inset-7 md:inset-8 rounded-full bg-slate-900 flex flex-col items-center justify-center text-xs">
                    <span className="text-[10px] md:text-[11px] text-slate-400">
                      Total
                    </span>
                    <span className="text-lg font-semibold">36K</span>
                  </div>
                </div>
                <div className="mt-3 text-[11px] text-slate-300 space-y-1 text-center">
                  <p>On-Demand 28K (71%)</p>
                  <p>SavingsPlan 4K (11%) · Spot 4K (18%)</p>
                </div>
              </div>

              <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-4">
                <p className="text-xs text-slate-400 mb-2">
                  Instance Current Generation (Spend)
                </p>
                <div className="flex items-center justify-center py-3">
                  <span className="text-2xl font-semibold">$1.03K</span>
                </div>
                <p className="text-[11px] text-slate-400 text-center">
                  100% on current generation families
                </p>
              </div>
            </div>
          </div>

          {/* 底部：保留原来的 Cost Drivers + Insights，然后再放 Ticket 表格 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold">Top Cost Drivers</h2>
                <span className="text-[11px] text-slate-400">Last 30 days</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px] min-w-[320px]">
                  <thead className="text-slate-400 text-left">
                    <tr>
                      <th className="pb-2">Service</th>
                      <th className="pb-2">Account</th>
                      <th className="pb-2 text-right">Cost</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-200">
                    <tr className="border-t border-slate-800/80">
                      <td className="py-2">EC2 On-Demand</td>
                      <td className="py-2">prod-compute-01</td>
                      <td className="py-2 text-right">$3,420</td>
                    </tr>
                    <tr className="border-t border-slate-800/80">
                      <td className="py-2">EC2 SavingsPlan</td>
                      <td className="py-2">analytics-cluster</td>
                      <td className="py-2 text-right">$1,260</td>
                    </tr>
                    <tr className="border-t border-slate-800/80">
                      <td className="py-2">EC2 Spot</td>
                      <td className="py-2">ml-training-spot</td>
                      <td className="py-2 text-right">$520</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold">Optimization Insights</h2>
                <span className="text-[11px] text-emerald-400">
                  +$320 potential monthly savings
                </span>
              </div>
              <ul className="space-y-2 text-[11px]">
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-2 w-2 rounded-full bg-emerald-400" />
                  <p>
                    Convert 8 x m5.4xlarge On-Demand instances in{" "}
                    <span className="text-emerald-300">prod-compute-01</span> to
                    1-year SavingsPlan.
                  </p>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-2 w-2 rounded-full bg-amber-400" />
                  <p>
                    Consider Spot for nightly{" "}
                    <span className="text-emerald-300">ml-training-spot</span>{" "}
                    workloads (current utilization &lt; 4h / day).
                  </p>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-2 w-2 rounded-full bg-sky-400" />
                  <p>
                    Rightsize 3 x r5.8xlarge instances in{" "}
                    <span className="text-emerald-300">analytics-cluster</span>{" "}
                    (avg CPU &lt; 20%).
                  </p>
                </li>
              </ul>
            </div>
          </div>

          {/* ✅ 最底部：Ticket 表格（来自 API） */}
          <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-4 h-[220px] flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <h2 className="text-sm font-semibold">Ticket List</h2>
                <button
                  type="button"
                  onClick={() => {
                    setCreateError(null);
                    setCreateSuccess("");
                    setShowCreateModal(true);
                  }}
                  className="px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-xs font-medium"
                >
                  Add New Ticket
                </button>
              </div>
              {!loading && (
                <span className="text-[11px] text-slate-400">
                  Total: {ticketTotal}
                </span>
              )}
            </div>

            {/* Scrollable table area */}
            <div className="overflow-y-auto flex-1">
              {loading ? (
                <p className="text-xs text-slate-400">Loading...</p>
              ) : error ? (
                <p className="text-xs text-red-400">Error: {error}</p>
              ) : tickets.length === 0 ? (
                <p className="text-xs text-slate-400">No data found</p>
              ) : (
                <table className="w-full text-[12px] min-w-[800px]">
                  <thead className="text-slate-400 text-left">
                    <tr>
                      <th className="pb-2 w-12">#</th>
                      <th className="pb-2">Ticket ID</th>
                      <th className="pb-2">Status</th>
                      <th className="pb-2">Create Time</th>
                      <th className="pb-2">Update Time</th>
                      <th className="pb-2">Customer ID</th>
                      <th className="pb-2">Customer Name</th>
                      <th className="pb-2">Contact First Name</th>
                      <th className="pb-2">Contact Last Name</th>
                      <th className="pb-2">Contact Email</th>
                      <th className="pb-2">Device Serial No</th>
                      <th className="pb-2">Problem Description</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-200">
                    {tickets.map((row, index) => (
                      <tr
                        key={row.ticketId}
                        className="border-t border-slate-800/80"
                      >
                        <td className="py-2 text-slate-400">{index + 1}</td>
                        <td className="py-2">{row.ticketId}</td>
                        <td className="py-2">{row.ticketStatus}</td>
                        <td className="py-2">{row.ticketCreateTime}</td>
                        <td className="py-2">{row.ticketUpdateTime}</td>
                        <td className="py-2">{row.customId}</td>
                        <td className="py-2">{row.customName}</td>
                        <td className="py-2">{row.contactPersonFirstName}</td>
                        <td className="py-2">{row.contactPersonLastName}</td>
                        <td className="py-2">{row.contactPersonEmail}</td>
                        <td className="py-2">{row.deviceSn}</td>
                        <td className="py-2">{row.problemDescription}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Create ticket modal */}
        {showCreateModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
            onClick={() => setShowCreateModal(false)}
          >
            <div
              className="w-full max-w-4xl bg-slate-900 border border-slate-700 rounded-2xl p-4 md:p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-sm font-semibold">
                    New Ticket (Add Row)
                  </h2>
                  {createSuccess && (
                    <span className="text-[11px] text-emerald-400">
                      {createSuccess}
                    </span>
                  )}
                  {createError && (
                    <span className="ml-2 text-[11px] text-red-400">
                      {createError}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full border border-slate-700 hover:border-purple-500 hover:bg-slate-800 transition"
                >
                  <X size={16} />
                </button>
              </div>

              <form
                className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px]"
                onSubmit={handleCreateTicket}
              >
                <InputField
                  label="Customer ID (custom_id)"
                  name="customId"
                  value={form.customId}
                  onChange={handleInputChange}
                  required
                />
                <InputField
                  label="Customer Name (custom_name)"
                  name="customName"
                  value={form.customName}
                  onChange={handleInputChange}
                  required
                />
                <InputField
                  label="Contact First Name (contact_person_first_name)"
                  name="contactPersonFirstName"
                  value={form.contactPersonFirstName}
                  onChange={handleInputChange}
                  required
                />
                <InputField
                  label="Contact Last Name (contact_person_last_name)"
                  name="contactPersonLastName"
                  value={form.contactPersonLastName}
                  onChange={handleInputChange}
                  required
                />
                <InputField
                  label="Contact Email (contact_person_email, optional)"
                  name="contactPersonEmail"
                  value={form.contactPersonEmail}
                  onChange={handleInputChange}
                />
                <InputField
                  label="Device Serial No (device_SN)"
                  name="deviceSn"
                  value={form.deviceSn}
                  onChange={handleInputChange}
                  required
                />
                <div className="md:col-span-3">
                  <label className="block mb-1 text-slate-300">
                    Problem Description (problem_desc)
                  </label>
                  <textarea
                    name="problemDescription"
                    value={form.problemDescription}
                    onChange={handleInputChange}
                    className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-[11px] focus:outline-none focus:ring-1 focus:ring-purple-500"
                    rows={3}
                    required
                  />
                </div>

                <div className="md:col-span-3 flex items-center justify-end mt-1 gap-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-1.5 rounded-lg border border-slate-600 text-xs hover:bg-slate-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="px-4 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-xs font-medium"
                  >
                    {creating ? "Submitting..." : "Create Ticket"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

/* -------- 侧边栏内容 -------- */

const SidebarContent = ({ onClose, onLogout, userLabel }) => {
  const handleClick = () => {
    if (onClose) onClose();
  };

  const handleLogoutClick = () => {
    if (onClose) onClose();
    onLogout();
  };

  return (
    <>
      <div className="px-6 py-5 flex items-center gap-3 border-b border-purple-700/40">
        <div className="w-10 h-10 rounded-2xl bg-purple-500 flex items-center justify-center text-xl font-bold">
          AI
        </div>
        <div>
          <p className="text-sm font-semibold tracking-wide">AI NOVA</p>
          <p className="text-xs text-purple-200/80">Compute Dashboard</p>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 text-sm">
        <NavItem icon={BarChart3} label="Overview" active onClick={onClose} />
        {/* <NavItem icon={Cpu} label="Compute Usage" onClick={onClose} />
      <NavItem icon={Gauge} label="Cost & Savings" onClick={onClose} />
      <NavItem icon={CreditCard} label="Billing" onClick={onClose} />
      <NavItem icon={Settings} label="Settings" onClick={onClose} /> */}
      </nav>
    </>
  );
};

/* -------- 小组件们 -------- */

const NavItem = ({ icon: Icon, label, active, onClick }) => (
  <button
    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-xs font-medium transition ${
      active
        ? "bg-purple-600 text-white shadow-[0_0_0_1px_rgba(248,250,252,0.06)]"
        : "text-purple-100/70 hover:bg-purple-700/40 hover:text-white"
    }`}
    onClick={onClick}
  >
    <Icon size={16} />
    <span>{label}</span>
  </button>
);

const KpiCard = ({ title, primary, secondary, badge, variant = "default" }) => (
  <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-3 flex flex-col justify-between">
    <div className="flex items-center justify-between mb-2">
      <p className="text-[11px] text-slate-400">{title}</p>
      {badge && (
        <span
          className={`px-2 py-0.5 rounded-full text-[10px] ${
            variant === "danger"
              ? "bg-red-500/20 text-red-300 border border-red-500/40"
              : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
          }`}
        >
          {badge}
        </span>
      )}
    </div>
    <div>
      <p className="text-xl font-semibold break-all">{primary}</p>
      {secondary && (
        <p className="text-[11px] text-slate-400 mt-1">{secondary}</p>
      )}
    </div>
  </div>
);

const LegendDot = ({ label }) => (
  <div className="flex items-center gap-1">
    <span className="w-2 h-2 rounded-full bg-purple-400" />
    <span className="text-[10px] text-slate-400">{label}</span>
  </div>
);

const InputField = ({ label, name, value, onChange, required }) => (
  <div>
    <label className="block mb-1 text-slate-300">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <input
      name={name}
      value={value}
      onChange={onChange}
      className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-1.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-purple-500"
      required={required}
    />
  </div>
);
